import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Target, Users, Zap, Eye, Sparkles, ArrowRight } from 'lucide-react';

const MissionVisionSection = () => {
  const [activeCard, setActiveCard] = useState(null);

  const visionCards = [
    {
      id: 'mission',
      icon: Target,
      title: "Our Mission",
      shortDesc: "To assist restaurants in their journey of success.",
      fullDesc: "We aim to empower restaurants by providing the tools and support they need for long-term success. Our focus is on enhancing efficiency and streamlining operations. By fostering innovation, we help businesses improve customer experiences and reduce costs. Our mission is to be a reliable partner in driving growth through tailored technology solutions that deliver tangible results.",
      color: "#1b7840",
      gradient: "linear-gradient(135deg, #1b7840 0%, #155c32 100%)"
    },
    {
      id: 'who',
      icon: Users,
      title: "Who We Are",
      shortDesc: "Tech enthusiasts aimed to solve problems of restaurants.",
      fullDesc: "We are a group of passionate tech enthusiasts committed to solving problems for restaurants. Our team thrives on bringing innovative solutions to the food service industry. With a deep understanding of the industry's challenges, we strive to merge traditional business practices with modern technology, enabling businesses to adapt and grow in an ever-evolving digital landscape.",
      color: "#2a9d8f",
      gradient: "linear-gradient(135deg, #2a9d8f 0%, #1e7468 100%)"
    },
    {
      id: 'what',
      icon: Zap,
      title: "What We Do",
      shortDesc: "We give technology services to restaurants.",
      fullDesc: "We specialize in delivering technology services tailored to the unique needs of restaurants. Our offerings range from point-of-sale systems to inventory management solutions that enhance efficiency. Our goal is to simplify business processes, boost operational performance, and ultimately improve profitability. We enable our clients to focus on delivering great customer service while we handle the tech behind the scenes.",
      color: "#e76f51",
      gradient: "linear-gradient(135deg, #e76f51 0%, #d55a3a 100%)"
    }
  ];

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3
      }
    }
  };

  const cardVariants = {
    hidden: { 
      opacity: 0, 
      y: 60,
      scale: 0.9
    },
    visible: { 
      opacity: 1, 
      y: 0,
      scale: 1,
      transition: {
        duration: 0.7,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };

  return (
    <section className="relative py-24 md:py-32 px-6 bg-white overflow-hidden">
      {/* Subtle Background Pattern */}
      <div className="absolute inset-0 opacity-[0.02]">
        <div className="absolute top-0 left-0 w-full h-full" 
          style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, #1b7840 1px, transparent 0)`,
            backgroundSize: '48px 48px'
          }}
        />
      </div>

      {/* Decorative Background Blobs */}
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        whileInView={{ opacity: 0.04, scale: 1 }}
        transition={{ duration: 1.2 }}
        viewport={{ once: true }}
        className="absolute top-20 left-10 w-96 h-96 rounded-full bg-primary blur-3xl"
      />
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        whileInView={{ opacity: 0.04, scale: 1 }}
        transition={{ duration: 1.2, delay: 0.3 }}
        viewport={{ once: true }}
        className="absolute bottom-20 right-10 w-96 h-96 rounded-full bg-yellow-400 blur-3xl"
      />

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Header Section */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          viewport={{ once: true, margin: "-100px" }}
          className="text-center mb-16 md:mb-20"
        >
          {/* Small Tag */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 mb-6"
          >
            <Eye className="w-4 h-4 text-primary" />
            <span className="text-xs md:text-sm font-bold text-primary uppercase tracking-wider">
              Our Vision
            </span>
            <Sparkles className="w-4 h-4 text-primary" />
          </motion.div>

          {/* Main Vision Statement */}
          <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-extrabold text-gray-900 leading-[1.1] mb-8"
            style={{ letterSpacing: '-0.02em' }}
          >
            <span className="relative inline-block">
              <span className="relative z-10">Empowering</span>
              <svg
                className="absolute -bottom-2 left-0 w-full h-3"
                viewBox="0 0 200 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                preserveAspectRatio="none"
              >
                <motion.path
                  d="M2 8C50 2, 150 2, 198 8"
                  stroke="#1b7840"
                  strokeWidth="4"
                  strokeLinecap="round"
                  initial={{ pathLength: 0 }}
                  whileInView={{ pathLength: 1 }}
                  transition={{ duration: 1, delay: 0.6 }}
                  viewport={{ once: true }}
                />
              </svg>
            </span>
            <br className="block sm:hidden" />
            <span className="sm:inline"> Food & Retail</span>{" "}
            <br />
            <span className="text-gray-900">Enterprises</span>
          </h2>

          {/* Vision Description */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            viewport={{ once: true }}
            className="text-lg md:text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed"
          >
            We empower food and retail enterprises with specialised solutions derived from deep industry insight. 
            Our seasoned team tailors strategies, optimises operations, refines brand identities, and deploys 
            digital expertise to reshape success in the dynamic world of food and retail.
          </motion.p>
        </motion.div>

        {/* Interactive Cards Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10"
        >
          {visionCards.map((card, index) => {
            const Icon = card.icon;
            const isActive = activeCard === card.id;
            
            return (
              <motion.div
                key={card.id}
                variants={cardVariants}
                onHoverStart={() => setActiveCard(card.id)}
                onHoverEnd={() => setActiveCard(null)}
                className="relative group cursor-pointer"
              >
                {/* Main Card */}
                <motion.div
                  layout
                  className="relative bg-[#F6F4EF] rounded-3xl p-8 md:p-10 h-full overflow-hidden"
                  whileHover={{ 
                    y: -8,
                    transition: { duration: 0.3 }
                  }}
                >
                  {/* Gradient overlay on hover */}
                  <motion.div
                    className="absolute inset-0"
                    style={{ background: card.gradient }}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: isActive ? 0.05 : 0 }}
                    transition={{ duration: 0.3 }}
                  />

                  {/* Content */}
                  <div className="relative z-10">
                    {/* Icon with animated blob background */}
                    <motion.div
                      className="relative mb-6 inline-block"
                      whileHover={{ 
                        scale: 1.1,
                        rotate: [0, -5, 5, 0],
                        transition: { duration: 0.5 }
                      }}
                    >
                      {/* Blob SVG Background */}
                      <div className="absolute inset-0 -z-10">
                        <svg viewBox="0 0 200 200" className="w-24 h-24">
                          <motion.path
                            fill={`${card.color}15`}
                            d="M44.7,-76.4C58.8,-69.2,71.8,-59.1,79.6,-45.8C87.4,-32.6,90,-16.3,88.5,-0.9C87,14.6,81.4,29.2,73.1,42.8C64.8,56.4,53.8,69,39.8,76.8C25.8,84.6,8.8,87.6,-7.1,87.1C-23,86.6,-37.9,82.6,-51.4,74.9C-64.9,67.2,-77,55.8,-84.4,42.1C-91.8,28.4,-94.5,12.4,-93.2,-3.2C-91.9,-18.8,-86.6,-34,-77.9,-46.8C-69.2,-59.6,-57.1,-69.9,-43.3,-77.3C-29.5,-84.7,-14.7,-89.2,0.3,-89.7C15.3,-90.2,30.6,-86.6,44.7,-76.4Z"
                            transform="translate(100 100)"
                            initial={false}
                            animate={{
                              d: [
                                "M44.7,-76.4C58.8,-69.2,71.8,-59.1,79.6,-45.8C87.4,-32.6,90,-16.3,88.5,-0.9C87,14.6,81.4,29.2,73.1,42.8C64.8,56.4,53.8,69,39.8,76.8C25.8,84.6,8.8,87.6,-7.1,87.1C-23,86.6,-37.9,82.6,-51.4,74.9C-64.9,67.2,-77,55.8,-84.4,42.1C-91.8,28.4,-94.5,12.4,-93.2,-3.2C-91.9,-18.8,-86.6,-34,-77.9,-46.8C-69.2,-59.6,-57.1,-69.9,-43.3,-77.3C-29.5,-84.7,-14.7,-89.2,0.3,-89.7C15.3,-90.2,30.6,-86.6,44.7,-76.4Z",
                                "M37.8,-65.5C49.5,-58.3,59.7,-48.1,66.2,-35.8C72.7,-23.5,75.5,-9.1,74.3,4.9C73.1,18.9,68,32.5,59.4,43.2C50.8,53.9,38.7,61.7,25.3,67.1C11.9,72.5,-2.8,75.5,-17.3,73.8C-31.8,72.1,-46.1,65.7,-57.4,55.3C-68.7,44.9,-77,30.5,-79.8,15.1C-82.6,-0.3,-80,-16.7,-72.8,-30.3C-65.6,-43.9,-53.8,-54.7,-40.6,-61.3C-27.4,-67.9,-12.9,-70.3,0.7,-71.4C14.3,-72.5,26.1,-72.7,37.8,-65.5Z",
                                "M44.7,-76.4C58.8,-69.2,71.8,-59.1,79.6,-45.8C87.4,-32.6,90,-16.3,88.5,-0.9C87,14.6,81.4,29.2,73.1,42.8C64.8,56.4,53.8,69,39.8,76.8C25.8,84.6,8.8,87.6,-7.1,87.1C-23,86.6,-37.9,82.6,-51.4,74.9C-64.9,67.2,-77,55.8,-84.4,42.1C-91.8,28.4,-94.5,12.4,-93.2,-3.2C-91.9,-18.8,-86.6,-34,-77.9,-46.8C-69.2,-59.6,-57.1,-69.9,-43.3,-77.3C-29.5,-84.7,-14.7,-89.2,0.3,-89.7C15.3,-90.2,30.6,-86.6,44.7,-76.4Z"
                              ]
                            }}
                            transition={{
                              duration: 8,
                              repeat: Infinity,
                              ease: "easeInOut"
                            }}
                          />
                        </svg>
                      </div>

                      {/* Icon */}
                      <div 
                        className="relative w-24 h-24 rounded-2xl flex items-center justify-center"
                        style={{ backgroundColor: `${card.color}15` }}
                      >
                        <Icon 
                          className="w-12 h-12"
                          style={{ color: card.color }}
                          strokeWidth={1.5}
                        />
                      </div>

                      {/* Glow effect */}
                      <motion.div
                        className="absolute inset-0 rounded-2xl blur-xl -z-20"
                        style={{ backgroundColor: card.color }}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: isActive ? 0.3 : 0 }}
                        transition={{ duration: 0.3 }}
                      />
                    </motion.div>

                    {/* Title */}
                    <h3 
                      className="text-2xl md:text-3xl font-bold mb-4"
                      style={{ 
                        color: card.color,
                        letterSpacing: '-0.01em' 
                      }}
                    >
                      {card.title}
                    </h3>

                    {/* Short Description */}
                    <p className="text-gray-700 text-base md:text-lg leading-relaxed mb-6">
                      {card.shortDesc}
                    </p>

                    {/* Expandable Full Description */}
                    <AnimatePresence>
                      {isActive && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.3 }}
                          className="overflow-hidden"
                        >
                          <div className="pt-4 border-t border-gray-300">
                            <p className="text-gray-600 text-sm md:text-base leading-relaxed">
                              {card.fullDesc}
                            </p>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>

                    {/* Read More Indicator */}
                    <motion.div
                      className="flex items-center gap-2 mt-6 text-sm font-semibold"
                      style={{ color: card.color }}
                      animate={{ 
                        opacity: isActive ? 0 : 1 
                      }}
                    >
                      <span>Hover to learn more</span>
                      <motion.div
                        animate={{ x: [0, 5, 0] }}
                        transition={{ 
                          duration: 1.5, 
                          repeat: Infinity,
                          ease: "easeInOut" 
                        }}
                      >
                        <ArrowRight className="w-4 h-4" />
                      </motion.div>
                    </motion.div>
                  </div>

                  {/* Bottom accent line */}
                  <motion.div
                    className="absolute bottom-0 left-0 h-1 w-full"
                    style={{ background: card.gradient }}
                    initial={{ scaleX: 0 }}
                    whileInView={{ scaleX: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, delay: index * 0.2 }}
                  />
                </motion.div>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Bottom Stats or CTA (Optional) */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          viewport={{ once: true }}
          className="text-center mt-20 md:mt-24"
        >
          <p className="text-gray-600 text-base md:text-lg mb-8">
            Ready to transform your business with our expertise?
          </p>
          
          <motion.div
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <a
              href="/contact"
              className="inline-flex items-center gap-2 px-10 py-4 bg-primary hover:bg-primary-dark text-white rounded-full font-semibold text-lg shadow-lg hover:shadow-xl transition-all duration-300"
            >
              Let's Work Together
              <ArrowRight className="w-5 h-5" />
            </a>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default MissionVisionSection;